import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Trash2 } from "lucide-react";

import { formatNumber } from "@/utils/numberFormat";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";
import CustomInputNumber from "@/components/desktop/form/input/input-number/CustomInputNumber";
import CustomButton from "@/components/desktop/button/CustomButton";

import { useIODistribution, useSaveIODistribution } from "@/hooks/histories/useIODistribution";

import styles from "./io-distribution.module.scss";

const IODistribution = ({ isOpen, applicationHistoryId, listType = null, filters = null, onClose }) => {
	const [businessCenters, setBusinessCenters] = useState([]);
	const applicationHistoryIdNumber = Number(applicationHistoryId);

	const { data, isLoading } = useIODistribution(applicationHistoryIdNumber, {
		enabled: isOpen && Number.isFinite(applicationHistoryIdNumber) && applicationHistoryIdNumber > 0,
	});

	const saveMutation = useSaveIODistribution(applicationHistoryIdNumber, listType, filters);

	const availableCenterOptions = useMemo(() => {
		if (!data?.all_business_center) return [];
		const assignedIds = new Set(businessCenters.filter((center) => center.id).map((center) => center.id));
		return data.all_business_center
			.filter((center) => !assignedIds.has(center.id))
			.map((center) => ({ value: center.id, label: center.name, area: center.area }));
	}, [data?.all_business_center, businessCenters]);

	useEffect(() => {
		if (!data?.business_center) return;
		setBusinessCenters(
			data.business_center.map((center) => ({
				application_history_id: applicationHistoryIdNumber,
				id: center.id,
				name: center.name,
				total_ha: center.total_ha,
				used_ha: center.used_ha,
				percentage: center.percentage ?? 100,
			})),
		);
	}, [data?.business_center, applicationHistoryIdNumber]);

	useEffect(() => {
		if (!isOpen) {
			setBusinessCenters([]);
		}
	}, [isOpen]);

	const handleRemoveCenter = (index) => {
		setBusinessCenters((current) => current.filter((_, centerIndex) => centerIndex !== index));
	};

	const handleAddCenter = () => {
		setBusinessCenters((current) => [
			...current,
			{
				application_history_id: applicationHistoryIdNumber,
				id: null,
				name: "",
				total_ha: 0,
				used_ha: 0,
				percentage: 0,
			},
		]);
	};

	const handleSelectCenter = (index, centerValue) => {
		if (!centerValue) return;
		const selectedCenter = data?.all_business_center?.find((center) => center.id === centerValue);
		if (!selectedCenter) return;

		setBusinessCenters((current) => {
			const updated = [...current];
			updated[index] = {
				...updated[index],
				id: selectedCenter.id,
				name: selectedCenter.name,
				total_ha: selectedCenter.area,
				used_ha: selectedCenter.area,
				percentage: 100,
			};
			return updated;
		});
	};

	const handlePercentageChange = (index, newPercentage) => {
		const parsedPercentage = newPercentage === null ? 0 : newPercentage;
		setBusinessCenters((current) => {
			const updated = [...current];
			updated[index] = {
				...updated[index],
				percentage: parsedPercentage,
				used_ha: (updated[index].total_ha * parsedPercentage) / 100,
			};
			return updated;
		});
	};

	const handleSave = async () => {
		if (!Number.isFinite(applicationHistoryIdNumber) || applicationHistoryIdNumber <= 0) {
			toast.error("ID de aplicación inválido para guardar distribución.");
			return;
		}

		const hasInvalidPercentage = businessCenters.some((center) => Number.isNaN(center.percentage) || center.percentage < 0 || center.percentage > 100);

		if (hasInvalidPercentage) {
			toast.warning("El porcentaje debe ser un número entre 0 y 100");
			return;
		}

		try {
			const normalizedBusinessCenters = businessCenters.map((center) => ({
				...center,
				application_history_id: applicationHistoryIdNumber,
				id: center.id === null ? null : Number(center.id),
				percentage: Number(center.percentage),
				used_ha: Number(center.used_ha),
				total_ha: Number(center.total_ha),
			}));

			await saveMutation.mutateAsync(normalizedBusinessCenters);
			toast.success("Distribución guardada exitosamente");
			onClose();
		} catch {
			toast.error("Error al guardar la distribución");
		}
	};

	return (
		<CustomModalForm
			open={isOpen}
			title="Distribución por Centros de Costo"
			onClose={onClose}
			showSave={false}
			size="lg"
			gridTable={false}
			isLoading={saveMutation.isLoading}
			processingMessage="Guardando distribución de centros de costo..."
			footer={
				<>
					<CustomButton variant="secondary" onClick={onClose} disabled={saveMutation.isLoading}>
						Cerrar
					</CustomButton>
					<CustomButton variant="primary" onClick={handleSave} loading={saveMutation.isLoading}>
						Guardar
					</CustomButton>
				</>
			}
		>
			{isLoading ? (
				<CustomFetching center message="Cargando distribución..." />
			) : (
				<>
					<table className={styles.table}>
						<thead>
							<tr className={styles.headerRow}>
								<th className={styles.headerCell}>Centro de Costo</th>
								<th className={styles.headerCell}>Ha. Centro de Costo</th>
								<th className={styles.headerCell}>Ha. Aplicadas</th>
								<th className={styles.headerCell}>% de Uso</th>
								<th className={styles.headerCell} />
							</tr>
						</thead>
						<tbody>
							{businessCenters.map((center, index) => (
								<tr key={`${center.id ?? "new"}-${index}`} className={styles.bodyRow}>
									<td className={styles.cellLeft}>
										{center.id ? (
											<span className={styles.centerName}>{center.name}</span>
										) : (
											<CustomInputSelect
												options={availableCenterOptions}
												value={null}
												onChange={(selectedValue) => handleSelectCenter(index, selectedValue)}
												placeholder="Seleccione un centro de costo"
												isClearable={false}
											/>
										)}
									</td>
									<td className={styles.cellRight}>{formatNumber(center.total_ha, 2)}</td>
									<td className={styles.cellRight}>{formatNumber(center.used_ha, 2)}</td>
									<td className={styles.cellRight}>
										<CustomInputNumber
											name={`percentage-${index}`}
											value={center.percentage}
											onChange={(value) => handlePercentageChange(index, value)}
											size="sm"
										/>
									</td>
									<td className={styles.cellAction}>
										<CustomButton size="sm" variant="danger" onClick={() => handleRemoveCenter(index)} startIcon={<Trash2 size={12} />} />
									</td>
								</tr>
							))}

							{businessCenters.length === 0 && (
								<tr>
									<td colSpan={5} className={styles.emptyRow}>
										No hay centros de costo asignados
									</td>
								</tr>
							)}
						</tbody>
					</table>

					<div className={styles.addButton}>
						<CustomButton variant="neutral" size="sm" onClick={handleAddCenter}>
							Agregar Centro de Costo
						</CustomButton>
					</div>
				</>
			)}
		</CustomModalForm>
	);
};

export default IODistribution;
